import type { FC, ReactNode } from 'react'
import type { GovernorateOption } from '@/types'

import { useState } from 'react'
import { Clock, Gift, Truck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/lib/i18n'
import { Select } from '@/components/ui/field'
import { CmsText } from '@/components/store/cms-text'
import { Num, Price } from '@/components/store/price'

interface ShippingEstimatorProps {
    governorates: Array<GovernorateOption>
    freeShippingEnabled: boolean
    freeShippingThreshold: number
    className?: string
}

export const ShippingEstimator: FC<ShippingEstimatorProps> = ({ governorates, freeShippingEnabled, freeShippingThreshold, className }): ReactNode => {
    const { tx, txn } = useI18n()
    const [selectedId, setSelectedId] = useState(governorates[0]?.id ?? '')
    const selected = governorates.find(governorate => governorate.id === selectedId) ?? null

    if (governorates.length === 0) return null

    return (
        <div className={cn('flex flex-col gap-5 rounded-3xl bg-white p-6 shadow-soft ring-1 ring-navy-100 sm:p-8', className)}>
            <div className='flex flex-col gap-2'>
                <label htmlFor='shipping-estimator' className='text-sm font-semibold text-navy-900'>{tx('اختر محافظتك', 'Choose your governorate')}</label>
                <Select id='shipping-estimator' value={selectedId} onChange={event => setSelectedId(event.target.value)}>
                    {governorates.map(governorate => (
                        <option key={governorate.id} value={governorate.id}>{governorate.name.v}</option>
                    ))}
                </Select>
            </div>

            {selected && (
                <div className='grid gap-3 sm:grid-cols-2' aria-live='polite'>
                    <div className='flex items-center gap-3 rounded-2xl bg-navy-50 p-4'>
                        <span className='flex size-11 shrink-0 items-center justify-center rounded-2xl bg-navy-900 text-cyan-300'>
                            <Truck className='size-5' aria-hidden='true' />
                        </span>
                        <div className='flex min-w-0 flex-col'>
                            <span className='truncate text-xs text-muted-foreground'>
                                {tx('تكلفة الشحن إلى', 'Shipping to')}
                                {' '}
                                <CmsText table='governorates' id={selected.id} field={selected.name} />
                            </span>
                            <Price value={selected.fee} className='text-xl font-bold text-navy-950' />
                        </div>
                    </div>

                    <div className='flex items-center gap-3 rounded-2xl bg-navy-50 p-4'>
                        <span className='flex size-11 shrink-0 items-center justify-center rounded-2xl bg-white text-navy-800 ring-1 ring-navy-100'>
                            <Clock className='size-5' aria-hidden='true' />
                        </span>
                        <div className='flex flex-col'>
                            <span className='text-xs text-muted-foreground'>{tx('مدة التوصيل المتوقعة', 'Estimated delivery')}</span>
                            <span className='text-base font-semibold text-navy-950'>
                                {txn('{min} إلى {max} أيام', '{min} to {max} days', { min: <Num value={selected.minDays} />, max: <Num value={selected.maxDays} /> })}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {freeShippingEnabled && freeShippingThreshold > 0 && (
                <p className='flex items-center gap-2.5 rounded-2xl bg-emerald-50 px-4 py-3 text-sm text-emerald-800 ring-1 ring-emerald-100'>
                    <Gift className='size-4.5 shrink-0' aria-hidden='true' />
                    <span>{txn('الشحن مجاني لأي محافظة للطلبات فوق {amount}', 'Free shipping to any governorate on orders over {amount}', { amount: <strong className='font-semibold'><Price value={freeShippingThreshold} /></strong> })}</span>
                </p>
            )}
        </div>
    )
}
